import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrainingFollowButton } from './TrainingFollowButton';
import { TrainingFeedRoutine } from '@/hooks/useTrainingFeed';
import { formatDistanceToNow } from 'date-fns';
import { Dumbbell, Utensils, Heart, ChevronDown, ChevronUp } from 'lucide-react';

interface FeedRoutineCardProps {
  routine: TrainingFeedRoutine;
  onLike: (routineId: string, hasLiked: boolean) => void;
  isLiking?: boolean;
  currentUserId?: string;
}

export const FeedRoutineCard: React.FC<FeedRoutineCardProps> = ({ 
  routine, 
  onLike,
  isLiking,
  currentUserId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isWorkout = routine.category === 'workout';
  const CategoryIcon = isWorkout ? Dumbbell : Utensils;
  const timeAgo = formatDistanceToNow(new Date(routine.created_at), { addSuffix: true });

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Parse routine data
  const routineData = routine.routine_data || {};
  const exercises = routineData.exercises || [];
  const items = routineData.items || [];
  const previewCount = 3;

  const handleLike = () => {
    if (currentUserId) {
      onLike(routine.id, routine.user_has_liked || false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <Link to={`/athletes/${routine.profile?.username || routine.profile_id}`}>
              <Avatar className="h-10 w-10">
                <AvatarImage src={routine.profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(routine.profile?.name)}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <Link 
                to={`/athletes/${routine.profile?.username || routine.profile_id}`}
                className="font-semibold text-foreground hover:text-primary transition-colors"
              >
                {routine.profile?.name || 'Athlete'}
              </Link>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </div>
          <TrainingFollowButton targetProfileId={routine.profile_id} />
        </div>

        {/* Category Badge */}
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className={isWorkout ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600'}>
            <CategoryIcon className="h-3 w-3 mr-1" />
            {isWorkout ? 'Workout' : 'Nutrition'}
          </Badge>
          <Badge variant="outline" className="font-normal">
            Routine
          </Badge>
        </div>

        <h3 className="font-medium text-foreground mb-2">{routine.title}</h3>

        {routine.description && (
          <p className="text-sm text-muted-foreground mb-3">{routine.description}</p>
        )}

        {/* Routine Preview */}
        <div className="bg-muted/50 rounded-lg p-3 mb-3">
          {isWorkout && exercises.length > 0 && (
            <div className="space-y-2">
              {exercises.slice(0, expanded ? undefined : previewCount).map((exercise: any, idx: number) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-foreground">{exercise.name}</span>
                  <span className="text-muted-foreground">
                    {exercise.sets}×{exercise.reps}
                    {exercise.notes && ` • ${exercise.notes}`}
                  </span>
                </div>
              ))}
              {exercises.length > previewCount && !expanded && (
                <p className="text-xs text-muted-foreground">
                  +{exercises.length - previewCount} more exercises
                </p>
              )}
            </div>
          )}

          {!isWorkout && items.length > 0 && (
            <div className="space-y-2">
              {routineData.meal_type && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  {routineData.meal_type}
                </p>
              )}
              {items.slice(0, expanded ? undefined : previewCount).map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-foreground">{item.name}</span>
                  {item.amount && (
                    <span className="text-muted-foreground">{item.amount}</span>
                  )}
                </div>
              ))}
              {items.length > previewCount && !expanded && (
                <p className="text-xs text-muted-foreground">
                  +{items.length - previewCount} more items
                </p>
              )}
            </div>
          )}
        </div>

        {/* Expand Button */}
        {((isWorkout && exercises.length > previewCount) || (!isWorkout && items.length > previewCount)) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full mb-3"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                View Full Routine
              </>
            )}
          </Button>
        )}

        {/* Media */}
        {routine.media_urls && routine.media_urls.length > 0 && (
          <div className="mb-3">
            <img 
              src={routine.media_urls[0]} 
              alt="Routine media"
              className="rounded-lg w-full max-h-48 object-cover"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLike}
            disabled={isLiking || !currentUserId}
            className={routine.user_has_liked ? 'text-red-500' : ''}
          >
            <Heart className={`h-4 w-4 mr-1 ${routine.user_has_liked ? 'fill-current' : ''}`} />
            {routine.likes_count || 0}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
